import { DEFAULT_EINVOICE_ADDRESS_SCHEME_TYPE, EntityType, getDefaultAddressSchemeTypeByCountry } from '@components'

import { Company, FrenchCompany } from '@modules-deprecated/app/companies/types'
import { getCompanyName } from '@modules-deprecated/app/companies/utils/getCompanyName'

import { computeVatNumber } from '../../companies/utils/computeVatNumber'
import { getSirenFromSiret } from '../../companies/utils/getSirenFromSiret'
import { isFrenchCompany } from '../../companies/utils/isFrenchCompany'
import { isFrenchPublicCompany } from '../../companies/utils/isFrenchPublicCompany'
import { CustomerType } from '../enums/customerType'
import { CustomerForm } from '../types/customerForm'

export const createCustomerFormFromCompany = (company: Partial<Company> | Partial<FrenchCompany>): CustomerForm => {
  if (isFrenchCompany(company)) {
    return createCustomerFormFromFrenchCompany(company)
  }

  return {
    address: {
      ...getCommonAddressFields(company),
      streetAddress1: [company.street, company.number].filter(Boolean).join(' '),
    },
    businessId: company.id ?? '',
    companyName: getCompanyName(company) ?? '',
    customerType: CustomerType.Company,
    eInvoiceAddress: { address: '', scheme: DEFAULT_EINVOICE_ADDRESS_SCHEME_TYPE },
    entityType: EntityType.Private,
  }
}

const createCustomerFormFromFrenchCompany = (company: Partial<FrenchCompany>): CustomerForm => {
  const isPublicEntity = isFrenchPublicCompany(company)
  const entityType = isPublicEntity ? EntityType.Public : EntityType.Private
  const eInvoiceScheme = getDefaultAddressSchemeTypeByCountry(isPublicEntity, 'FR')
  // Public entity e-invoicing isn't supported yet, so we leave the address blank — the field
  // is disabled and shows a "coming soon" placeholder. Private entities are pre-filled with the
  // SIREN, matching the default SIRENE scheme.
  const eInvoiceAddress = isPublicEntity ? '' : (company.siren ?? getSirenFromSiret(company.siret) ?? '')

  return {
    address: {
      ...getCommonAddressFields(company),
      streetAddress1: company.street ?? '',
    },
    businessId: company.siret ?? '',
    companyName: getCompanyName(company) ?? '',
    customerType: CustomerType.Company,
    eInvoiceAddress: { address: eInvoiceAddress, scheme: eInvoiceScheme },
    entityType,
    vatNumber: computeVatNumber('FR', company.siret) ?? undefined,
  }
}

const getCommonAddressFields = (company: Partial<Company> | Partial<FrenchCompany>) => ({
  city: company.city ?? '',
  pobox: company.zipcode ?? '',
  ...('country' in company && company.country && { country: company.country }),
})
